/**
 * Pre-order badge sync — keep the "Pre Order" badge in lock-step with the Globo PreOrder app.
 *
 * Globo exposes its live offers ONLY client-side in window.GloboPreorderParams.preorderSettings:
 *   - products    { productId: { variantId: profileId } }   → "Specific products" offers
 *   - collections { collectionId: profileId }               → "Specific collection" offers
 *   - profiles    { profileId: { status, ... } }            → status 1 = Ongoing, 0 = ended
 *
 * The theme renders a "Pre Order" badge server-side (snippets/product-card-badges.liquid,
 * blocks/_product-media-gallery.liquid) only for products carrying a Globo "Pre Order" SELLING
 * PLAN. That covers "Specific products" offers, but:
 *   1) the selling plan lingers after an offer ends → the server can OVER-show on ended offers;
 *   2) "Specific collection" offers attach NO selling plan → the server NEVER shows them, even
 *      while Ongoing (the reported bug).
 *
 * So this script is the single source of truth on the client:
 *   - REMOVE the badge from any card/PDP whose offer is not Ongoing (status !== 1);
 *   - ADD the badge to any AVAILABLE card/PDP that belongs to a product- or collection-based
 *     offer that IS Ongoing but wasn't rendered server-side.
 *
 * Matching uses data-product-id + data-product-collections rendered onto the cards and the PDP
 * media-gallery. If the Globo data is missing or in an unexpected shape it does nothing
 * (fail-open) — a badge never appears or disappears just because Globo failed to load.
 */
(function () {
  var PREORDER_LABEL = 'Pre Order';

  // Build the set of products and collections whose Globo offer is currently Ongoing.
  // Returns { products: {id:true}, collections: {id:true} } or null when data isn't ready.
  function buildActiveSets() {
    var params = window.GloboPreorderParams;
    var settings = params && params.preorderSettings;
    if (!settings || !settings.profiles) return null;

    var profiles = settings.profiles;
    var productMap = settings.products || {}; // { productId: { variantId: profileId } }
    var collectionMap = settings.collections || {}; // { collectionId: profileId }

    // Globo populates these maps in several steps; wait until profiles plus at least one offer
    // map are present so we never gate against half-loaded data.
    if (!Object.keys(profiles).length) return null;
    if (!Object.keys(productMap).length && !Object.keys(collectionMap).length) return null;

    function isOngoing(profileId) {
      var profile = profiles[profileId];
      return !!profile && Number(profile.status) === 1;
    }

    var products = Object.create(null);
    Object.keys(productMap).forEach(function (productId) {
      var variantToProfile = productMap[productId] || {};
      var active = Object.keys(variantToProfile).some(function (variantId) {
        return isOngoing(variantToProfile[variantId]);
      });
      if (active) products[String(productId)] = true;
    });

    var collections = Object.create(null);
    Object.keys(collectionMap).forEach(function (collectionId) {
      if (isOngoing(collectionMap[collectionId])) collections[String(collectionId)] = true;
    });

    return { products: products, collections: collections };
  }

  function readIds(el, attr) {
    var raw = el.getAttribute(attr);
    if (!raw) return [];
    return raw.split(',').filter(Boolean);
  }

  // Does this element (card or gallery) belong to an Ongoing pre-order offer?
  function isPreorderActive(el, active) {
    var productId = String(el.getAttribute('data-product-id') || '');
    if (productId && active.products[productId]) return true;
    var collectionIds = readIds(el, 'data-product-collections');
    for (var i = 0; i < collectionIds.length; i++) {
      if (active.collections[collectionIds[i]]) return true;
    }
    return false;
  }

  function isAvailable(el) {
    return el.getAttribute('data-product-available') === 'true';
  }

  // --- Product cards (collection grid, recommendations, featured, search) ---------------------

  function addCardBadge(card) {
    card.classList.add('product-card--preorder');
    card.setAttribute('data-product-preorder', 'true');

    var container = card.querySelector('.product-badges');
    if (!container) return;
    if (container.querySelector('.product-badges__badge--preorder')) return; // already there

    // Pre-order takes priority over a Sale badge (matches snippets/product-card-badges.liquid).
    container.querySelectorAll('.product-badges__badge:not(.product-badges__badge--preorder)').forEach(function (b) {
      b.remove();
    });

    container.classList.add('product-badges--preorder');
    var badge = document.createElement('div');
    badge.className = 'product-badges__badge product-badges__badge--rectangle product-badges__badge--preorder';
    badge.textContent = PREORDER_LABEL;
    container.appendChild(badge);
  }

  function removeCardBadge(card) {
    card.classList.remove('product-card--preorder');
    card.setAttribute('data-product-preorder', 'false');
    var badge = card.querySelector('.product-badges__badge--preorder');
    if (badge) {
      var container = badge.closest('.product-badges');
      if (container) container.classList.remove('product-badges--preorder');
      badge.remove();
    }
  }

  function gateCards(active) {
    document.querySelectorAll('product-card[data-product-id]').forEach(function (card) {
      var wantsBadge = isPreorderActive(card, active) && isAvailable(card);
      var hasBadge = card.classList.contains('product-card--preorder');
      if (wantsBadge && !hasBadge) {
        addCardBadge(card);
      } else if (!wantsBadge && hasBadge) {
        // Ended offer with a lingering selling plan → restore the card to a normal product
        // (quick-add comes back).
        removeCardBadge(card);
      }
      // The badge is hidden by CSS until this attribute is set (see the [data-preorder-gated]
      // rule in snippets/product-card-badges.liquid). Reveal it now that this card matches the
      // live Globo data, so the page never flashes a badge that we are about to remove/add.
      card.setAttribute('data-preorder-gated', '');
    });
  }

  // --- Product detail page gallery badge ------------------------------------------------------

  function addGalleryBadge(gallery) {
    if (gallery.querySelector('.product-detail-badge--preorder')) return; // already there
    var badge = document.createElement('div');
    badge.className = 'product-detail-badge product-detail-badge--preorder';
    badge.setAttribute('data-product-id', gallery.getAttribute('data-product-id') || '');
    badge.textContent = PREORDER_LABEL;
    gallery.appendChild(badge);
  }

  function gateGalleries(active) {
    document.querySelectorAll('media-gallery[data-product-id]').forEach(function (gallery) {
      var existing = gallery.querySelector('.product-detail-badge--preorder');
      // Only add over an AVAILABLE product — a sold-out product keeps its "Out of Stock" badge,
      // mirroring the server-side rule in blocks/_product-media-gallery.liquid.
      var wantsBadge = isPreorderActive(gallery, active) && isAvailable(gallery);
      if (wantsBadge) {
        addGalleryBadge(gallery);
      } else if (existing) {
        existing.remove();
      }
      // Reveal the (reconciled) PDP badge — hidden by CSS until this attribute is set.
      gallery.setAttribute('data-preorder-gated', '');
    });
  }

  function gate() {
    var active = buildActiveSets();
    if (!active) return; // data not ready / unexpected → leave server-rendered badges untouched
    gateCards(active);
    gateGalleries(active);
  }

  var scheduled = false;
  function scheduleGate() {
    if (scheduled) return;
    scheduled = true;
    (window.requestAnimationFrame || window.setTimeout)(function () {
      scheduled = false;
      gate();
    }, 0);
  }

  function init() {
    gate();
    // Re-run when the grid/PDP is re-rendered (filtering, sorting, pagination, infinite scroll,
    // recommendations, variant switches). Cheap: schedules one batched pass per insertion.
    if (typeof MutationObserver === 'function') {
      var watched = 'product-card[data-product-id], media-gallery[data-product-id]';
      new MutationObserver(function (mutations) {
        for (var i = 0; i < mutations.length; i++) {
          var added = mutations[i].addedNodes;
          for (var j = 0; j < added.length; j++) {
            var node = added[j];
            if (node.nodeType !== 1) continue;
            if (
              (node.matches && node.matches(watched)) ||
              (node.querySelector && node.querySelector(watched))
            ) {
              scheduleGate();
              return;
            }
          }
        }
      }).observe(document.body, { childList: true, subtree: true });
    }
  }

  function start() {
    var attempts = 0;
    (function waitForGloboData() {
      if (buildActiveSets()) {
        init();
      } else if (attempts++ < 60) {
        // Globo populates preorderSettings via an inline script that may run after us; poll
        // briefly (up to ~9s) until the data is ready, then gate.
        setTimeout(waitForGloboData, 150);
      } else {
        // Give up (fail-open): Globo's data never loaded, so reveal the server-rendered badges
        // rather than leaving an Ongoing pre-order hidden behind the anti-flash CSS. This flips
        // the [data-preorder-gated] CSS off for every card/gallery at once.
        document.documentElement.classList.add('preorder-gate-disabled');
      }
    })();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
